import { useMemo } from 'react';
import agent from '@root/agent';
import Loader from '@root/components/common/Loader';
import CveDetails from '@root/pages/workloload-scanning/CveDetails';
import useEnhancedQuery from '@root/query/useEnhancedQuery';

// "CVE details" tab of the Findings side overlay: the same Details view as the Kubernetes CVEs overlay opened from
// the CVEs tab (../../Sideoverlay with a `cveScope`). Its only source is the Kubernetes `cves-details` API, scoped
// to the finding's own environment and cluster; the CSPM `vulnerability-details` lookup CveDetails would otherwise
// make is skipped by handing it this response.
const CveDetailsTab = ({ selectedRow }) => {
  const { data: cveDetailsData, isFetching: isFetchingCveDetails } = useEnhancedQuery(
    ['get_k8_cves_details', 'details', selectedRow?.account_id, selectedRow?.cluster, selectedRow?.cve_id],
    () =>
      agent.Vulnerabilites.get_k8_cves_details({
        environments: selectedRow?.account_id ? [selectedRow.account_id] : [],
        clusters: selectedRow?.cluster ? [selectedRow.cluster] : [],
        cve_id: selectedRow?.cve_id,
        tab: 'details',
        risk_spotlight: selectedRow?.risk_spotlight || false,
      }),
    { enabled: Boolean(selectedRow?.cve_id) },
  );

  const detailValue = cveDetailsData?.data;

  // The finding row under the field names CveDetails reads. The CVE fields the row lacks (threat trend, CVSS
  // details, references, ...) are filled from the cves-details response; the row's own values win.
  const cveDetailsRow = useMemo(() => {
    const row = { ...selectedRow };
    if (detailValue && typeof detailValue === 'object') {
      Object.entries(detailValue).forEach(([key, value]) => {
        if (row[key] == null) row[key] = value;
      });
    }

    return {
      ...row,
      fixed_in_version: row.fixed_in_version ?? row.fix_version,
      first_seen: row.first_seen ?? row.discovered_at,
      vulnerability_details: {
        description: detailValue?.description ?? row.description,
        ...row.vulnerability_details,
      },
    };
  }, [detailValue, selectedRow]);

  if (isFetchingCveDetails) return <Loader />;

  return <CveDetails selectedRow={cveDetailsRow} vulnerabilityDetails={cveDetailsData ?? null} />;
};

export default CveDetailsTab;
